import { environment } from '../../../../environments/environment';
import { SupplyPreview } from '../../../services/benchmarkService';

/**
 * Reading a flow run's context stream: the server-sent events the orchestrator
 * publishes as each node completes, proxied by the control plane at
 * /agent/context/stream.
 *
 * Read with fetch rather than EventSource, which cannot send the bearer token
 * the proxy requires.
 */

export interface StreamFrame {
    /** The SSE event name; absent for ordinary data frames. */
    event?: string;
    data: string;
}

/**
 * Splits buffered SSE text into complete frames, returning what is left over
 * for the next chunk. Comment lines (the orchestrator's keep-alives) are
 * dropped.
 */
export function parseSseFrames(buffer: string): { frames: StreamFrame[]; rest: string } {
    const normalised = buffer.replace(/\r\n/g, '\n');
    const blocks = normalised.split('\n\n');
    const rest = blocks.pop() ?? '';
    const frames: StreamFrame[] = [];
    for (const block of blocks) {
        let event: string | undefined;
        const data: string[] = [];
        for (const line of block.split('\n')) {
            if (!line || line.startsWith(':')) continue;
            const colon = line.indexOf(':');
            const field = colon === -1 ? line : line.slice(0, colon);
            const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
            if (field === 'event') event = value;
            else if (field === 'data') data.push(value);
        }
        if (event || data.length) frames.push({ event, data: data.join('\n') });
    }
    return { frames, rest };
}

/** The supplied-sample loader's activity, whatever build of it ran. */
const LOADER_PREFIX = 'LOAD_SUPPLIED_BENCHMARK_SAMPLE';
const FAILURE_OBSERVATION = 'Benchmark Sample Failure';

export interface LoaderOutcome {
    preview: SupplyPreview | null;
    /** The loader's refusal, verbatim. */
    refusal: string | null;
}

const activityCodeOf = (event: Record<string, any>): string =>
    String(event.activity_code ?? event.activityCode ?? event.activity_template_code ?? event.activityTemplateCode ?? '');

/**
 * What the loader reported, if this event is the loader's: either the preview
 * of how the sample was read, or the reason it was refused. Null for any other
 * node's event.
 */
export function loaderOutcome(event: unknown): LoaderOutcome | null {
    if (!event || typeof event !== 'object') return null;
    const e = event as Record<string, any>;
    const observations: any[] = Array.isArray(e.observations) ? e.observations : [];
    const failure = observations.find((o) => o && o.observation_type === FAILURE_OBSERVATION);
    const preview = e.benchmark_preview && typeof e.benchmark_preview === 'object' ? e.benchmark_preview as SupplyPreview : null;
    const isLoader = activityCodeOf(e).startsWith(LOADER_PREFIX);

    if (!isLoader && !preview && !failure) return null;
    if (!preview && !failure) return null;
    return {
        preview,
        refusal: failure ? String(failure.description ?? 'The sample was refused.') : null
    };
}

export interface ContextStreamHandlers {
    onEvent: (event: unknown) => void;
    onDone: () => void;
    signal: AbortSignal;
}

/**
 * Follows a run's context stream until the orchestrator says it is done, or
 * the signal aborts. Rejects if the stream cannot be opened.
 */
export async function followContextStream(sessionId: string, { onEvent, onDone, signal }: ContextStreamHandlers): Promise<void> {
    const token = localStorage.getItem('token');
    const response = await fetch(
        `${environment.controlplaneUrl}/agent/context/stream?session_id=${encodeURIComponent(sessionId)}`,
        {
            headers: { Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            signal
        }
    );
    if (!response.ok || !response.body) {
        throw new Error(`The run's progress could not be followed (${response.status}).`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { frames, rest } = parseSseFrames(buffer);
        buffer = rest;
        for (const frame of frames) {
            if (frame.event === 'done') {
                onDone();
                reader.cancel().catch(() => undefined);
                return;
            }
            if (!frame.data) continue;
            try {
                onEvent(JSON.parse(frame.data));
            } catch {
                // A frame that is not JSON carries nothing to act on.
            }
        }
    }
    onDone();
}
